import React, { createContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

type GuardedNavigateFn = (path: string) => void;

interface NavigationGuardContextValue {
  registerGuard: (fn: GuardedNavigateFn) => void;
  unregisterGuard: () => void;
  navigate: (path: string) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const guardRef = useRef<GuardedNavigateFn | null>(null);

  const registerGuard = useCallback((fn: GuardedNavigateFn) => {
    guardRef.current = fn;
  }, []);

  const unregisterGuard = useCallback(() => {
    guardRef.current = null;
  }, []);

  const guardedNavigate = useCallback((path: string) => {
    if (guardRef.current) {
      guardRef.current(path);
    } else {
      navigate(path);
    }
  }, [navigate]);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard, navigate: guardedNavigate }}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

export { NavigationGuardContext };
export default NavigationGuardContext;
